import React from "react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Trash2, Pencil, Plus, Pin } from "lucide-react";
import ThreadsRuangPanel from "@/components/admin/ThreadsRuangPanel";
import RuangAdminPanel from "@/components/admin/RuangAdminPanel";
import ForumConsole from "@/components/site/ForumConsole";

/**
 * Ruang & Diskusi — konsolidasi admin surface untuk Forum Ruang.
 * Sub-tabs: Ruang · Diskusi · Balasan · Aktivitas.
 * Kategori forum lama dipindahkan ke Legacy Mapping (super admin only).
 */
export default function ForumTab({
  isModOrAbove,
  isAdminOrSuper,
  isSuper,
  communityTab,
  setCommunityTab,
  allThreads,
  allReplies,
  catLabel,
  togglePin,
  deleteThread,
  deleteReplyMod,
  onThreadsReload,
  initialFilterRuang,
  onFilterRuangConsumed,
  setActiveTab,
}) {
  if (!isModOrAbove) return null;

  const tabBtn = (v, label, testid) => (
    <button
      onClick={() => setCommunityTab(v)}
      data-testid={testid}
      className={"px-4 py-1.5 rounded-full text-sm border transition-colors " + (communityTab === v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#4B5563] border-[#EAE7E0] hover:bg-[#F3F1ED]")}
    >{label}</button>
  );

  return (
    <TabsContent value="community" className="mt-6" data-testid="community-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {isAdminOrSuper && tabBtn("ruang", "Ruang", "community-tab-ruang")}
        {tabBtn("threads", `Diskusi (${allThreads.length})`, "community-tab-threads")}
        {tabBtn("replies", `Balasan (${allReplies.length})`, "community-tab-replies")}
        {isAdminOrSuper && tabBtn("aktivitas", "Aktivitas", "community-tab-aktivitas")}
      </div>

      {/* Ruang — reuse RuangAdminPanel. Klik ikon threads pada row → jump ke tab Diskusi difilter. */}
      {communityTab === "ruang" && isAdminOrSuper && (
        <div data-testid="community-ruang-panel">
          <RuangAdminPanel onOpenThreadsWithFilter={(rid) => {
            onFilterRuangConsumed?.(rid);
            setCommunityTab("threads");
          }} />
        </div>
      )}

      {communityTab === "threads" && (
        <ThreadsRuangPanel
          threads={allThreads}
          catLabel={catLabel}
          isAdminOrSuper={isAdminOrSuper}
          togglePin={togglePin}
          deleteThread={deleteThread}
          onReload={onThreadsReload}
          initialFilterRuang={initialFilterRuang}
          onFilterRuangConsumed={onFilterRuangConsumed}
        />
      )}

      {communityTab === "replies" && (
        <div data-testid="community-replies-panel" className="space-y-3">
          <p className="text-sm text-[#4B5563]">200 balasan terbaru dari semua Forum. Hapus balasan yang tidak pantas.</p>
          {allReplies.map((r, i) => (
            <div key={r.reply_id} data-testid={`reply-row-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">
                    <a href={`/forum/${r.thread_id}#reply-${r.reply_id}`} target="_blank" rel="noreferrer" className="hover:text-[#E07A5F]">{r.thread_title}</a>
                    {" · "}{catLabel(r.thread_category)}{" · "}{r.user_name}{" · "}{new Date(r.created_at).toLocaleDateString("id-ID")}
                  </div>
                  <p className="mt-2 text-sm text-[#1F2937] leading-relaxed whitespace-pre-wrap line-clamp-3">{r.body}</p>
                </div>
                <Button variant="outline" onClick={() => deleteReplyMod(r)} data-testid={`delete-reply-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C]">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          {allReplies.length === 0 && <div className="text-[#4B5563] text-sm p-4">Belum ada balasan.</div>}
        </div>
      )}

      {/* Aktivitas — reuse ForumConsole tapi force ke tab Aktivitas Live saja. */}
      {communityTab === "aktivitas" && isAdminOrSuper && (
        <div data-testid="community-aktivitas-panel">
          <ForumConsole initialTab="aktivitas" hideStrukturTab />
        </div>
      )}
    </TabsContent>
  );
}
